/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Button, Callout, Checkbox } from "@octopusdeploy/design-system-components";
import { ArrowDownToSquareIcon, ArrowUpFromSquareIcon } from "@octopusdeploy/design-system-icons";
import type { LicenseStatusResource, StatsResourceCollection, TaskResource, TelemetryConfigurationResource } from "@octopusdeploy/octopus-server-client";
import { TaskName } from "@octopusdeploy/octopus-server-client";
import type { AnalyticsLinkClickedDispatcher } from "@octopusdeploy/portal-routes";
import { links, nameForLink, useDispatchLinkClicked } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import { cloneDeep } from "lodash";
import moment from "moment";
import React from "react";
import { client, repository } from "~/clientInstance";
import type { Refresh } from "~/components/DataBaseComponent/DataBaseComponent";
import FormBaseComponent from "~/components/FormBaseComponent";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import ExternalLink from "~/components/Navigation/ExternalLink";
import InternalLink from "~/components/Navigation/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { PagingList } from "~/components/PagingList/PagingList";
import Section from "~/components/Section";
import TaskDetails from "~/components/TaskDetails";
import { withTheme } from "~/components/Theme";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { ExpandableFormSection, Summary } from "~/components/form";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import DateFormatter from "~/utils/DateFormatter";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import styles from "./style.module.less";
type TelemetryPageInternalProps = {
    freeTierEnabled: boolean;
    dispatchLinkClicked: AnalyticsLinkClickedDispatcher;
};
interface TelemetryPageInternalState extends OptionalFormBaseComponentState<TelemetryConfigurationResource> {
    lastRunTask?: TaskResource;
    taskHistory?: StatsResourceCollection;
    licenseStatus?: LicenseStatusResource;
}
class TelemetryTasksList extends PagingList<TaskResource<{}>> {
}
const buildTaskRow = (task: TaskResource<{}>) => <TaskDetails task={task} stripTopBottomPadding={true}/>;
class TelemetryPageInternal extends FormBaseComponent<TelemetryPageInternalProps, TelemetryPageInternalState, TelemetryConfigurationResource> {
    constructor(props: TelemetryPageInternalProps) {
        super(props);
        this.state = {};
    }
    async componentDidMount() {
        localStorage.setItem(TelemetryHasBeenIntroducedLocalStorageKey, "true");
        await this.doBusyTask(async () => {
            const configuration = await repository.TelemetryConfiguration.get();
            const task = await repository.Telemetry.getLatestTelemetryTask();
            const taskHistory = await repository.Tasks.list({ name: TaskName.SendTelemetry, skip: 0, take: 10 });
            const licenseStatus = await repository.Licenses.getCurrentStatus();
            this.doRefresh = await this.startRefreshLoop(() => this.refreshLatestTask(), 5000);
            this.setState({
                model: configuration,
                cleanModel: cloneDeep(configuration),
                lastRunTask: task,
                taskHistory,
                licenseStatus,
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    async loadTaskHistory(skip: number = 0) {
        const taskHistory = await repository.Tasks.list({ taskName: TaskName.SendTelemetry, skip: skip, take: 10 });
        this.setState({ taskHistory });
    }
    async refreshLatestTask() {
        const lastRunTask = await repository.Telemetry.getLatestTelemetryTask();
        const taskHistory = await repository.Tasks.list({ name: TaskName.SendTelemetry, skip: 0, take: 10 });
        return {
            ...this.state,
            lastRunTask,
            taskHistory,
        };
    }
    handleSaveClick = async () => {
        await this.doBusyTask(async () => {
            const result = await repository.TelemetryConfiguration.modify(this.state.model!);
            this.setState({
                model: result,
                cleanModel: cloneDeep(result),
            });
        });
    };
    sendTelemetry = async () => {
        const task = await repository.Tasks.createSendTelemetryTask();
        this.setState({ lastRunTask: task });
    };
    renderLastTelemetryTaskDetails = () => {
        const { lastRunTask } = this.state;
        if (!lastRunTask)
            return <span>Telemetry task has not run</span>;
        if (lastRunTask.StartTime)
            return (<span>
                    Telemetry task last ran{` `}
                    <InternalLink to={links.taskPage.generateUrl({ taskId: lastRunTask.Id })} className={styles.taskTime}>
                        {DateFormatter.momentAgo(lastRunTask.StartTime!)}
                    </InternalLink>
                </span>);
        return <span>Telemetry task is in the task queue</span>;
    };
    renderTelemetryDetails = () => {
        return (<ExpandableFormSection key="IsTelemetryReportingEnabled" errorKey="IsTelemetryReportingEnabled" title="Telemetry Reporting" summary={this.state.model!.Enabled ? Summary.default("Enabled") : Summary.summary("Disabled")} help="Send telemetry data to Octopus" isExpandedByDefault expandable>
                {this.props.freeTierEnabled && this.state.model?.IsTelemetryEnforced && this.renderUpsellCallout()}

                <Checkbox value={!!this.state.model?.Enabled} onChange={(Enabled) => this.setModelState({ Enabled })} label="Send telemetry" disabled={this.state.model?.IsTelemetryEnforced}/>

                <div className={styles.actionButtonWrap}>
                    <ExternalLink href={client.resolveLinkTemplate("TelemetryDownload", {})} download showIcon={false}>
                        <Button label="Download Telemetry Preview" onClick={() => { }} importance={"secondary"} size="small" icon={<ArrowDownToSquareIcon size={20}/>}/>
                    </ExternalLink>
                </div>

                {!this.state.model?.Enabled && <div>When telemetry reporting is turned off, some data is collected locally but not sent to Octopus. You can send a one-time telemetry snapshot to Octopus.</div>}

                {!this.state.model?.Enabled && (<div className={styles.actionButtonWrap}>
                        <Button label="Send Telemetry Snapshot" disabled={!!this.state.busy} onClick={this.sendTelemetry} importance="secondary" size="small" icon={<ArrowUpFromSquareIcon size={20}/>}/>
                    </div>)}
            </ExpandableFormSection>);
    };
    renderUpsellCallout = () => {
        const upgradeLinkLabel = "Upgrade";
        const upgradeLink = OctoLink.Create(`plan-upgrade?serial=${this.state.licenseStatus?.SerialNumber}`);
        return (<Callout type="upsell" hideTitle={true}>
                <div>Upgrade to the Professional or Enterprise tier to turn off telemetry reporting.</div>
                <div>
                    <ExternalLink href={upgradeLink} trackAnalytics={false} onClick={() => this.props.dispatchLinkClicked(nameForLink(), { linkLabel: upgradeLinkLabel, documentUrl: upgradeLink })}>
                        {upgradeLinkLabel}
                    </ExternalLink>
                </div>
            </Callout>);
    };
    renderTaskHistory = () => {
        return (<ExpandableFormSection key="TelemetryHistory" errorKey="TelemetryHistory" title="Telemetry History" summary={Summary.summary(this.renderLastTelemetryTaskDetails())} help={"View a history of telemetry tasks"} isExpandedByDefault={true} expandable>
                <TelemetryTasksList initialData={this.state.taskHistory} onRow={(item: TaskResource<{}>) => buildTaskRow(item)} onRowRedirectUrl={(task: TaskResource<{}>) => links.taskPage.generateUrl({ taskId: task.Id })} showPagingInNumberedStyle={true} currentPageIndex={0} onPageSelected={async (skip: number) => {
                await this.loadTaskHistory(skip);
            }}/>
            </ExpandableFormSection>);
    };
    renderNotification = () => {
        const now = moment();
        const ShowAsNewUntil = moment(this.state.model?.ShowAsNewUntil);
        return (ShowAsNewUntil.isAfter(now) && (<Callout type={"new-feature"} title={"Telemetry changes"}>
                    <p>We've changed the way Octopus Deploy collects and sends telemetry{";"} for better transparency and visibility.</p>
                    <p>This now occurs via a system task. You can enable or disable telemetry reporting, view the status, and preview your data on this page.</p>
                    <p>Telemetry reporting is enabled by default. Data will be sent from {ShowAsNewUntil.locale("en").format("dddd[,] DD MMMM YYYY[,] HH:mm:ss [GMT]ZZ")} unless you disable it before then.</p>
                </Callout>));
    };
    render() {
        return withTheme(() => (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} onSaveClick={this.handleSaveClick}>
                {({ FormContent, createSaveAction }) => (<PageContent header={{ primaryAction: createSaveAction({}), title: "Telemetry" }} busy={this.state.busy}>
                        <FormContent expandAllOnMount={false} hideExpandAll={true}>
                            {this.state.model && (<TransitionAnimation>
                                    {this.renderNotification()}
                                    <Section>
                                        <div>Octopus collects telemetry data on user actions, feature usage and performance to improve the user experience and guide roadmap decisions.</div>
                                        <div>Rest assured, we don’t collect personal data or sensitive information, for example, source code, configuration files, variables, or personally identifiable information (PII).</div>
                                        <div>
                                            You can download a preview of your telemetry data below or read our <ExternalLink href="Telemetry">documentation</ExternalLink> to learn how we collect telemetry.
                                        </div>
                                    </Section>
                                    {this.renderTelemetryDetails()}
                                    {this.renderTaskHistory()}
                                </TransitionAnimation>)}
                        </FormContent>
                    </PageContent>)}
            </LegacyForm>));
    }
    private doRefresh: Refresh = () => Promise.resolve();
    static displayName = "TelemetryPageInternal";
}
export function TelemetryPage() {
    const freeTierEnabled = useOctopusFeatureToggle("free-tier", false);
    const dispatchLinkClicked = useDispatchLinkClicked();
    return <TelemetryPageInternal freeTierEnabled={freeTierEnabled} dispatchLinkClicked={dispatchLinkClicked}/>;
}
export const TelemetryHasBeenIntroducedLocalStorageKey = "TelemetryHasBeenIntroduced";
